// BRIEF 86 PHASE 2, STEP 9 (run by brief 90 §5) — the Template-subtasks button,
// once, for both entity modals.
//
// 177 lines in `AgendaEventModal` and 171 in `TaskModal`, and — measured with
// the pair census's normalisation — IDENTICAL apart from six substitutions and
// two comments. Not a resemblance: the same button, twice, down to the
// `boxSizing: "border-box"` that brief 87 §6 E2 had to argue about once and then
// copy.
//
// THE SIX, and every one of them is the entity, not a decision:
//   the taxonomy it reads templates from   `entries`  (event types / task types)
//   the field holding the date             `dateField` ("date" / "dueDate")
//   the missing-date toast                 keyed off `kind`
//   the rows it reads existing subtasks from and writes back to
//                                          `rows` / `setRows`
//   the parent column on the new subtask   `parentKey`
//
// TWO SHAPES IN ONE BUTTON, and they are not variants: an UNSAVED parent has no
// id to hang a subtask on, so the toggle only rewrites `form.subtasks` locally
// (`_reconcileTpl`); a SAVED one POSTs each missing template row through
// `createSubtask` and merges the server's rows into the store. Both hosts had
// both, in the same order, with the same three refusals (no type/date, no
// template for the type, all present already).
import { _buildTplItems, _reconcileTpl } from "../../lib/templatePreview";
import { createSubtask } from "../../api";
import { resolveSubtaskText } from "../../lib/subtaskHelpers";
import { FS, RAD } from "../../lib/tokens";
import type { TFn, TaxList } from "../../lib/taxonomyTypes";
// Rule 1 — generated, not authored: the row this posts and merges is the
// database's own shape (server/prisma/schema.prisma -> shared/entities.ts), and
// `resolveSubtaskText` already speaks it.
import type { Subtask as SubtaskRow } from "@shared/entities";
type TplItem = {
  text: string;
  textEn?: string | null;
  textFr?: string | null;
  textNl?: string | null;
  dueDate?: string | null;
};
type Row = { id?: number; subtasks?: SubtaskRow[]; [k: string]: unknown };
type FormShape = {
  id?: number | null;
  type?: string | null;
  date?: string | null;
  dueDate?: string | null;
  subtasks?: SubtaskRow[];
  templatesOn?: boolean;
  [k: string]: unknown;
};

type Props = {
  // 'event' | 'task' — reaches the date field, the parent column and one toast.
  kind: "event" | "task";
  form: FormShape;
  setForm: (fn: (p: FormShape) => FormShape) => void;
  // The type taxonomy whose entries carry the templates.
  entries: TaxList;
  // The host's store of parents — agendaEvents or tasks — and its setter. Only
  // the SAVED arm touches them.
  rows: Row[];
  setRows: (fn: (prev: Row[]) => Row[]) => void;
  t: TFn;
  toast: (m: string) => void;
};

export default function TemplateSubtasksButton({
  kind,
  form,
  setForm,
  entries,
  rows,
  setRows,
  t,
  toast,
}: Props) {
  const dateField = kind === "task" ? "dueDate" : "date";
  const parentKey = kind === "task" ? "parentTaskId" : "parentEventId";
  return (
    <>
      {!form.id ? (
        <button
          type="button"
          onClick={() => {
            setForm((p: FormShape) => {
              const next = !(p.templatesOn !== false);
              const tpl = next
                ? _buildTplItems(entries, p.type, p[dateField])
                : [];
              return {
                ...p,
                templatesOn: next,
                subtasks: _reconcileTpl(p.subtasks, tpl, next),
              };
            });
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "5px 10px",
            fontSize: FS.s11,
            borderRadius: RAD.r8,
            border: "1px solid var(--br)",
            background:
              form.templatesOn !== false ? "var(--as)" : "var(--bi)",
            color:
              form.templatesOn !== false ? "var(--ac)" : "var(--ts)",
            cursor: "pointer",
            fontFamily: "inherit",
            fontWeight: 600,
            minHeight: "var(--field-h,30px)",
            // BRIEF 87 §6 E2 — MEASURED, and the "in-between width,
            // and two different ones" is exactly one property. Task's
            // twin (TaskModal.tsx:561-563) carries width:100% +
            // border-box and therefore spans its grid cell; Event's
            // carried neither, so `display:inline-flex` sized it to
            // its own text. That is the ~272 vs ~437 in the brief:
            // content-width against cell-width, not two chosen
            // numbers.
            //
            // ONE WIDTH, FULL, and it is TASK's — because Task's is
            // the one already snapped to the grid, so §7 T2 ("identical
            // to E2") costs no change there at all.
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: RAD.pill,
              background:
                form.templatesOn !== false ? "var(--ac)" : "var(--tm)",
              flexShrink: 0,
            }}
          />
          {t("subtask.template_toggle", {
            fallback: "Template subtasks",
          })}
        </button>
      ) : (
        <button
          type="button"
          onClick={async () => {
            if (!form.type || !form[dateField]) {
              toast(
                kind === "task"
                  ? t("toast.tpl_need_type_due", {
                      fallback: "⚠ Set type + due date first",
                    })
                  : t("toast.tpl_need_type_date", {
                      fallback: "⚠ Set type + date first",
                    }),
              );
              return;
            }
            const ex =
              rows.find((x: Row) => x.id === form.id)?.subtasks || [];
            const tpl = _buildTplItems(entries, form.type, form[dateField]);
            if (!tpl.length) {
              toast(
                t("toast.tpl_none_for_type", {
                  fallback: "No template for this type",
                }),
              );
              return;
            }
            const norm = (s: string | null | undefined) => (s || "").trim().toLowerCase();
            const seen = new Set(
              ex.map((s: SubtaskRow) => norm(resolveSubtaskText(s))),
            );
            const adds = tpl.filter(
              (tt: TplItem) => !seen.has(norm(tt.text)),
            );
            if (!adds.length) {
              toast(
                t("toast.tpl_all_present", {
                  fallback: "All template subtasks already present",
                }),
              );
              return;
            }
            const maxOrd = ex.reduce(
              (m: number, s: SubtaskRow) => Math.max(m, s.sortOrder || 0),
              -1,
            );
            const made: SubtaskRow[] = [];
            try {
              for (let i = 0; i < adds.length; i++) {
                const a = adds[i];
                const r = (await createSubtask({
                  [parentKey]: form.id,
                  text: a.text,
                  textEn: a.textEn || null,
                  textFr: a.textFr || null,
                  textNl: a.textNl || null,
                  dueDate: a.dueDate || null,
                  sortOrder: maxOrd + 1 + i,
                })) as SubtaskRow;
                made.push(r);
              }
              setRows((prev: Row[]) =>
                prev.map((x: Row) =>
                  x.id === form.id
                    ? { ...x, subtasks: [...ex, ...made] }
                    : x,
                ),
              );
              toast(
                t("toast.subtasks_injected", {
                  count: made.length,
                  fallback: `Added ${made.length} subtask${made.length === 1 ? "" : "s"}`,
                }),
              );
            } catch (err: unknown) {
              toast(
                "⚠ " +
                  ((err as Error)?.message ||
                    t("toast.tpl_insert_failed", {
                      fallback: "Insert failed",
                    })),
              );
            }
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "5px 10px",
            fontSize: FS.s11,
            borderRadius: RAD.r8,
            border: "1px solid var(--br)",
            background: "var(--bi)",
            color: "var(--ts)",
            cursor: "pointer",
            fontFamily: "inherit",
            fontWeight: 600,
            minHeight: "var(--field-h,30px)",
            width: "100%",
            boxSizing: "border-box",   // §6 E2, see the first button
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: RAD.pill,
              background: "var(--ac)",
              flexShrink: 0,
            }}
          />
          {t("subtask.insert_template_button", {
            fallback: "Insert template subtasks",
          })}
        </button>
      )}
    </>
  );
}
